import { type ReactElement, useState } from "react";

import { Button } from "@/common/ui/button";

interface LoginPageProps {
  isSignedOut: boolean;
  onSignIn: () => Promise<void>;
}

export function LoginPage({ isSignedOut, onSignIn }: LoginPageProps): ReactElement {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async (): Promise<void> => {
    setIsSigningIn(true);
    setError(null);
    try {
      await onSignIn();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sign in failed. Please try again.";
      setError(message);
      setIsSigningIn(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-2 text-center">
          <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded bg-[#C2785C]">
            <span className="text-lg font-bold text-white">E</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isSignedOut ? "Signed out" : "Endure Ops Console"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isSignedOut
              ? "You have been signed out successfully."
              : "Sign in with your @letsendure.com account"}
          </p>
        </div>

        {error ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-center text-sm text-destructive">
            {error}
          </div>
        ) : null}

        <Button
          type="button"
          data-testid="sign-in-button"
          className="w-full"
          variant="outline"
          disabled={isSigningIn}
          onClick={() => void handleSignIn()}
        >
          {isSigningIn ? "Redirecting..." : "Continue with Google"}
        </Button>
      </div>
    </div>
  );
}
